import { z } from 'zod';
import { Api } from '@automapa/svelte-automapa-template';
import { JobCountSchema, ConnectionRequestSchema, ConnectionResponseSchema } from './types';
import type { ConnectionRequestType } from './types';
import type { JobCountType } from './types';
import type { ConnectionResponseType } from './types';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { type QueriesType, QueriesTypeSchema } from '../telematics/types';
import {
    AutoSuggesterRequestSchema,
    type AutoSuggesterRequestType,
    AutoSuggesterResponseSchema,
    type AutoSuggesterResponseType,
} from '../telematics/components/FilterByParameters/featureAutoSuggester';
import { FriendlyDateGenerator } from '../../utilities/FriendlyDateGenerator';
import { ApiInterface } from '../../utilities/ApiInterface';
import type { LocationResponseType } from '../Location/types';

export const ModifyValuesSchema = z.record(z.string(), z.any());
export type ModifyValuesType = z.infer<typeof ModifyValuesSchema>;

export const ModifyRequestSchema = z.object({
    query: QueriesTypeSchema,
    values: ModifyValuesSchema,
});
export type ModifyRequestType = z.infer<typeof ModifyRequestSchema>;

export const ModifyResponseSchema = z.object({
    modified: z.number(),
});
export type ModifyResponseType = z.infer<typeof ModifyResponseSchema>;

export const queryAdhoc = {
    // process_status: { $in: [0,1]},
};

export const queryOverdue = {
    // process_status: { $in: [0,1]},
    target_date: { $lt: FriendlyDateGenerator.getTodayYYYYMMDD() },
};

export const getQueryAdhoc = (selectedDay: string, planning_started: string) => {
    return {
        created: { $gt: planning_started },
        target_date: selectedDay,
    };
};

export const JobHistoryEventResponseSchema = z.object({
    event_id: z.number(),
    event_type: z.string(),
    event_description: z.string().catch(''),
    event_time: z.string(),
    user_id: z.number().nullable().catch(null),
    user_email: z.string().nullable().catch(null),
    diff_data: z.any(),
    extra_data: z.any(),
});
export type JobHistoryEventResponseType = z.infer<typeof JobHistoryEventResponseSchema>;

export class ConnectionApi extends ApiInterface {
    public static async getAll(api: Api, f: ApiFilterBuilder): Promise<ConnectionResponseType[]> {
        return this._getAll(api, f, 'connections', ConnectionResponseSchema);
    }
    public static async getWithQueries(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder
    ): Promise<ConnectionResponseType[]> {
        return api.getData<QueriesType, ConnectionResponseType[]>(
            'post',
            '/connection/query' + filter.getUri(),
            queries,
            QueriesTypeSchema,
            z.array(ConnectionResponseSchema)
        );
    }

    public static async getCountWithQueries(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder
    ): Promise<JobCountType> {
        return api.getData<QueriesType, JobCountType>(
            'post',
            '/connection/count/query' + filter.getUri(),
            queries,
            QueriesTypeSchema,
            JobCountSchema
        );
    }

    public static async modify(
        api: Api,
        query: QueriesType,
        values: ModifyValuesType
    ): Promise<ModifyResponseType> {
        return api.getData<ModifyRequestType, ModifyResponseType>(
            'post',
            '/connection/modify',
            { query, values },
            ModifyRequestSchema,
            ModifyResponseSchema
        );
    }

    public static async getOne(api: Api, jobId: number): Promise<ConnectionResponseType> {
        return api.getData<undefined, ConnectionResponseType>(
            'get',
            `/connection/${jobId}`,
            undefined,
            z.undefined(),
            ConnectionResponseSchema
        );
    }

    public static async updateOne(
        api: Api,
        jobId: number,
        job: ConnectionRequestType
    ): Promise<ConnectionResponseType> {
        return api.getData<ConnectionRequestType, ConnectionResponseType>(
            'put',
            `/connection/${jobId}`,
            job,
            ConnectionRequestSchema,
            ConnectionResponseSchema
        );
    }

    public static async filter(
        api: Api,
        filter: ApiFilterBuilder
    ): Promise<ConnectionResponseType[]> {
        return api.getData<undefined, ConnectionResponseType[]>(
            'get',
            `/connection` + filter.getUri(),
            undefined,
            z.undefined(),
            z.array(ConnectionResponseSchema)
        );
    }

    public static async removeAll(api: Api) {
        return this.removeWithQuery(api, { $and: [{}, { id: { $nin: [] } }] });
    }

    public static async removeOne(api: Api, id: number) {
        return api.delete(`/connection/${id}`);
    }

    public static async removeWithQuery(api: Api, query: QueriesType) {
        return api.getData<QueriesType, { removed: number }>(
            'post',
            `/connection/remove`,
            query,
            QueriesTypeSchema,
            z.object({ removed: z.number() })
        );
    }

    public static async createOne(
        api: Api,
        job: ConnectionRequestType
    ): Promise<ConnectionResponseType> {
        return api.getData<ConnectionRequestType, ConnectionResponseType>(
            'post',
            '/connection',
            job,
            ConnectionRequestSchema,
            ConnectionResponseSchema
        );
    }

    public static async revgeo(api: Api, x: number, y: number): Promise<any> {
        return api.getData<undefined, any>(
            'get',
            '/location/revgeo?x=' + x + '&y=' + y,
            undefined,
            z.any(),
            z.any()
        );
    }
}
